import { create } from 'zustand';

import { showErrorToast } from '@repo/ui/components/ui/error-toast';
import { createInitialState, InitialState } from '@repo/ui/state';
import { getPhotoVogueService } from '@/services/photovogue';
import type { Photographer } from '@/services/photovogue';

export interface PhotographerStore extends InitialState<Photographer | null> {
  fetch: (id: string) => Promise<void>;
  updating: boolean;
  moderate: (state: Photographer['state']) => Promise<void>;
  updateSpotlight: (spotlight: boolean) => Promise<void>;
  reset: () => void;
}

export const initialState = {
  updating: false,
  ...createInitialState<Photographer | null>(null),
};

let requestVersion = 0;

export const store = create<PhotographerStore>((set, store) => ({
  ...initialState,

  fetch: async (id) => {
    const version = ++requestVersion;
    set((state) => ({ ...state, data: null, loading: true, error: null }));
    try {
      const service = getPhotoVogueService();
      const [photographer, details] = await Promise.all([
        service.getPhotographer(id),
        service.getPhotographerDetails(id),
      ]);
      if (version !== requestVersion) return;
      const data = { ...photographer, ...details };
      set((state) => ({ ...state, data, loading: false }));
    } catch (error) {
      if (version !== requestVersion) return;
      showErrorToast({ title: 'Unable to load photographer' });
      set((state) => ({
        ...state,
        error: (error as Error).message,
        loading: false,
      }));
    }
  },

  moderate: async (photographerState) => {
    const photographer = store().data;
    if (!photographer) return;
    const version = requestVersion;
    set((state) => ({ ...state, updating: true }));
    try {
      const data = await getPhotoVogueService().moderatePhotographer(
        photographer.id,
        photographerState,
      );
      if (version !== requestVersion) return;
      set((state) => ({
        ...state,
        data: { ...state.data, ...data },
        error: null,
      }));
    } catch (error) {
      if (version !== requestVersion) return;
      showErrorToast({ title: 'Unable to update photographer' });
      set((state) => ({ ...state, error: (error as Error).message }));
    } finally {
      if (version === requestVersion) {
        set((state) => ({ ...state, updating: false }));
      }
    }
  },

  updateSpotlight: async (spotlight) => {
    const photographer = store().data;
    if (!photographer) return;
    const version = requestVersion;
    set((state) => ({ ...state, updating: true }));
    try {
      const data = await getPhotoVogueService().updatePhotographerSpotlight(
        photographer.id,
        spotlight,
      );
      if (version !== requestVersion) return;
      set((state) => ({
        ...state,
        data: { ...state.data, ...data },
        error: null,
      }));
    } catch (error) {
      if (version !== requestVersion) return;
      showErrorToast({ title: 'Unable to update photographer spotlight' });
      set((state) => ({ ...state, error: (error as Error).message }));
    } finally {
      if (version === requestVersion) {
        set((state) => ({ ...state, updating: false }));
      }
    }
  },

  reset: () => {
    ++requestVersion;
    set((state) => ({ ...state, ...initialState }));
  },
}));
